import React, { useEffect, useRef, useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import {
  Close as CloseIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Modal,
  Slide,
  Switch,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { formatISO } from "date-fns";
import {
  compact,
  debounce,
  endsWith,
  find,
  get,
  mapValues,
  snakeCase,
  upperFirst,
} from "lodash";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { useParams } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import {
  BodyCompositionAttributes,
  BodyCompositionDetails_SettingFragment,
  BodyCompositionFieldsKind,
  CreateBodyCompositionsInput,
  UpdateSettingsInput,
} from "@/gql/graphql";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { displayError } from "@/utils/errors";
import { fraction } from "@/utils/number";

import { clientProfilePageQuery } from "../../../ClientProfilePage";

interface AddBodyCompositionModalType {
  onClose: () => void;
  visibility: boolean;
  isEditing: boolean;
  settings?: BodyCompositionDetails_SettingFragment;
  currentDate: Date;
}

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    alignItems: "center",
    justifyContent: "center",
    overflowY: "auto",
  },
  buttonContainer: {
    display: "flex",
    marginTop: "auto",
    alignSelf: "center",
    marginBottom: "20px",
  },
  customizeModalOpen: {
    marginRight: theme.spacing(0),
    marginLeft: theme.spacing(16),
  },
  customizeModalClose: {
    marginRight: "auto",
    marginLeft: "auto",
  },
  customizeModal: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#EDF0F5",
    marginTop: theme.spacing(4),
    marginRight: theme.spacing(16),
    padding: theme.spacing(1, 4),
    width: "40%",
    overflowY: "auto",
  },
  defaultBodyMeasurements: {
    display: "flex",
    backgroundColor: colors.white,
    flexDirection: "column",
    marginBottom: theme.spacing(2),
  },
  defaultBodyMeasurementsHeaderText: {
    margin: theme.spacing(2),
  },
  defaultFields: {
    display: "flex",
    marginBottom: 8,
  },
  error: {
    fontSize: theme.typography.pxToRem(12),
  },
  fields: {
    marginBottom: 12,
  },
  fieldWrapper: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    flexWrap: "wrap",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
  fieldNames: {
    fontSize: theme.typography.pxToRem(14),
    color: colors.grey,
  },
  modalBody: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: colors.white,
    padding: theme.spacing(2, 4),
    width: "60%",
    marginTop: theme.spacing(4),
  },
  modalHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: theme.spacing(8),
  },
  modalHeaderExpanded: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  modalHeaderText: {
    fontWeight: "bold",
  },
  switchWrapper: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
    marginLeft: theme.spacing(4),
  },
  scaleTextFieldLabel: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: "bold",
    marginBottom: 10,
  },
}));

const StyledButton = withStyles(Button, {
  root: {
    border: "1px solid #FFF",
  },
});

const StyledRestorButton = withStyles(Button, (theme: Theme) => ({
  root: {
    alignSelf: "center",
    margin: theme.spacing(4),
  },
}));

const StyledTextField = withStyles(TextField, {
  root: {
    border: "1px solid #DEE6F0",
  },
});

const StyledCancelButton = withStyles(Button, {
  root: {
    color: "#FF6262",
    border: "2px solid #FF6262",
    marginRight: 8,
  },
});

const StyledFormControlLabel = withStyles(FormControlLabel, (theme: Theme) => ({
  label: {
    color: colors.grey,
    fontSize: theme.typography.pxToRem(12),
  },
  root: {
    marginBottom: 8,
  },
}));

interface BodyCompositionFields {
  name: string;
  label: string;
  type: string;
  visibility: boolean;
  value: number | null;
}

const hasUpTo2Decimals = function (value: number | null | undefined): boolean {
  if (!value) return true;
  if (Number.isInteger(value)) return true;
  const result = value.toString().split(".")[1]
    ? value.toString().split(".")[1].length <= 2
    : true;
  return result;
};

type FormValues = {
  bodyCompositionFields: BodyCompositionFields[];
  date: Date;
};

const BODY_COMPOSITION_FIELDS: { [key: string]: string } = mapValues(
  BodyCompositionFieldsKind
);

const bodyCompositionByDateQuery = graphql(`
  query BodyCompositionByDate($id: ID!, $date: ISO8601Date!) {
    client(id: $id) {
      id
      bodyCompositionByDate(date: $date) {
        ...BodyCompositionDetails
      }
    }
  }
`);

const createBodyCompositionsMutation = graphql(`
  mutation CreateBodyCompositions($input: CreateBodyCompositionsInput!) {
    createBodyCompositions(input: $input) {
      bodyCompositions {
        ...BodyCompositionDetails
      }
    }
  }
`);

const updateBodyCompositionSettingMutation = graphql(`
  mutation UpdateBodyCompositionSetting($input: UpdateSettingsInput!) {
    updateSettings(input: $input) {
      settings {
        ...BodyCompositionDetails_Setting
      }
    }
  }
`);

const AddBodyCompositionModal: React.FC<AddBodyCompositionModalType> = ({
  onClose,
  visibility,
  isEditing,
  settings,
  currentDate,
}) => {
  const bodyCompositionFieldsVisibilityInitialValue = [
    {
      name: "bodyFatPercentage",
      label: "Body Fat Percentage",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "boneMassKg",
      label: "Bone Mass",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "fatFreeMassKg",
      label: "Fat Free Mass",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "fatFreeMassPercentage",
      label: "Fat Free Mass Percentage",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "fatMassKg",
      label: "Fat Mass",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "leanBodyMassKg",
      label: "Lean Body Mass",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "leanBodyMassPercentage",
      label: "Lean Body Mass Percentage",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "metabolicAge",
      label: "Metabolic Age",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "muscleMassKg",
      label: "Muscle Mass",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "muscleMassPercentage",
      label: "Muscle Mass Percentage",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "visceralFatKg",
      label: "Visceral Fat",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "visceralFatPercentage",
      label: "Visceral Fat Percentage",
      visibility: true,
      type: "defaultField",
      value: 0,
    },

    ////////

    {
      name: "bodyCellMassKg",
      label: "Body Cell Mass",
      visibility: false,
      type: "additionalField",
      value: null,
    },
    {
      name: "intraCellularWaterLiter",
      label: "Intra Cellular Water",
      visibility: false,
      type: "additionalField",
      value: null,
    },
    {
      name: "bodyWaterPercentage",
      label: "Body Water Percentage",
      visibility: false,
      type: "additionalField",
      value: null,
    },
    {
      name: "intraCellularWaterPercentage",
      label: "Intra Cellular Water Percentage",
      visibility: false,
      type: "additionalField",
      value: null,
    },
    {
      name: "boneMassPercentage",
      label: "Bone Mass Percentage",
      visibility: false,
      type: "additionalField",
      value: null,
    },
    {
      name: "residualMassKg",
      label: "Residual Mass",
      visibility: false,
      type: "additionalField",
      value: null,
    },
    {
      name: "extraCellularWaterLiter",
      label: "Extra Cellular Water",
      visibility: false,
      type: "additionalField",
      value: null,
    },
    {
      name: "totalBodyWaterLiter",
      label: "Total Body Water",
      visibility: false,
      type: "additionalField",
      value: null,
    },
    {
      name: "extraCellularWaterPercentage",
      label: "Extra Cellular Water Percentage",
      visibility: false,
      type: "additionalField",
      value: null,
    },
    {
      name: "residualMassPercentage",
      label: "Residual Mass Percentage",
      visibility: false,
      type: "additionalField",
      value: null,
    },
  ];

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors, isValid },
    control,
    reset,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      bodyCompositionFields: bodyCompositionFieldsVisibilityInitialValue,
      date: new Date(),
    },
    mode: "onBlur",
    reValidateMode: "onBlur",
  });
  const snackbar = useGlobalSnackbar();
  const { clientId } = useParams<{ clientId: string }>();
  const [updateBodyCompositionSetting] = useMutation(
    updateBodyCompositionSettingMutation
  );
  const [createBodyCompositions] = useMutation(createBodyCompositionsMutation);
  const modeLabel = isEditing ? "Edit" : "Add";
  const buttonLabel = isEditing ? "Save" : "Add";
  const { data, loading, refetch } = useQuery(bodyCompositionByDateQuery, {
    variables: {
      id: clientId ?? "",
      date: formatISO(currentDate, { representation: "date" }),
    },
  });
  const [isSavingSelection, setIsSavingSelection] = useState<boolean>(false);

  function getInputFieldValue(fieldName: string, value: number): number {
    if (endsWith(fieldName, "Percentage")) {
      return fraction(value);
    }
    return value;
  }

  const getVisibility = (fieldName: string): boolean => {
    return (
      find(settings?.bodyCompositionFields, { fieldName: snakeCase(fieldName) })
        ?.visible ?? false
    );
  };

  const { fields } = useFieldArray({ control, name: "bodyCompositionFields" });

  const controlledDate = watch("date");
  const date = useRef(controlledDate);

  function resetFields() {
    const bodyCompositions = data?.client?.bodyCompositionByDate;
    if (bodyCompositions) {
      const newValues = fields.map((bc) => ({
        ...bc,
        visibility: getVisibility(bc.name),
        value: (() => {
          const value = get(bodyCompositions, bc.name);
          if (value === null) return null;
          if (endsWith(bc.name, "Percentage")) {
            return Number(value) * 100;
          }
          return value;
        })(),
      }));
      setValue("bodyCompositionFields", [...newValues]);
    }
  }

  useEffect(() => {
    setValue("date", currentDate);
  }, [currentDate]);

  useEffect(() => {
    if (date.current != controlledDate) {
      debounce(async () => {
        const result = await refetch({
          date: formatISO(controlledDate, { representation: "date" }),
          id: clientId,
        });
        if (result) return;
      }, 1000)();
    }
    return () => {
      date.current = controlledDate;
    };
  }, [controlledDate]);

  useEffect(() => {
    let fetching = true;
    if (fetching) {
      resetFields();
    }
    return () => {
      fetching = false;
    };
  }, [
    settings?.bodyCompositionFields,
    data?.client?.bodyCompositionByDate,
    isEditing,
  ]);

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    const date = formatISO(values.date, { representation: "date" });
    const bodyCompositions = values.bodyCompositionFields.map((bc) => {
      if (bc.value && bc.visibility) {
        return {
          attr: snakeCase(bc.name),
          value: getInputFieldValue(bc.name, Number(bc.value)),
          date: date,
        };
      }
    });

    const attributes: BodyCompositionAttributes[] = [
      ...compact(bodyCompositions),
    ];
    const input: CreateBodyCompositionsInput = { clientId, attributes };
    try {
      const result = await createBodyCompositions({
        variables: { input },
        refetchQueries: [
          { query: clientProfilePageQuery, variables: { id: clientId ?? "" } },
          // refetchBodyCompositionsHistoryQuery({ id: clientId ?? "" }),
        ],
      });
      if (result) {
        snackbar.openSnackBar(
          `Successfully ${isEditing ? "updated" : "added"}`
        );

        onClose();
      }
    } catch (e) {
      snackbar.openSnackBar(
        displayError(
          e,
          `There was an error in ${
            isEditing ? "updating" : "adding"
          } your clients body composition`
        )
      );
    }
  };

  const { classes, cx } = useStyles();

  const [customizeModalVisible, setCustomizeModalVisible] =
    useState<boolean>(false);

  const handleUpdateClientSettings = async () => {
    setIsSavingSelection(true);
    const bodyCompositionFields: BodyCompositionFieldsKind[] = compact(
      fields.map((f) => {
        if (f.visibility)
          return BODY_COMPOSITION_FIELDS[
            upperFirst(f.name)
          ] as BodyCompositionFieldsKind;
      })
    );

    const input: UpdateSettingsInput = {
      clientId: clientId,
      attributes: {
        bodyCompositionFields: bodyCompositionFields,
      },
    };

    try {
      await updateBodyCompositionSetting({ variables: { input } });
    } catch (e) {
      snackbar.openSnackBar(
        displayError(e, "There was an error in saving your fields selection")
      );
    } finally {
      setIsSavingSelection(false);
      snackbar.openSnackBar("Selection saved.");
    }
  };

  function handleRestoreDefault() {
    const defaultSettings = fields.map((element) => {
      if (element.type === "defaultField") {
        return { ...element, visibility: true };
      } else {
        return { ...element, visibility: false };
      }
    });

    setValue("bodyCompositionFields", [...defaultSettings]);
  }

  function getErrorMessage(type: string | undefined) {
    switch (type) {
      case "max":
        return "Value for this field have exceeded 100";
      case "validate":
        return "Value must not exceed 2 decimal places";
      case "min":
        return "Must be a minimum value of 0";
      default:
        return "Please input a valid number";
    }
  }

  function displayBodyCompositionFields(type: string) {
    return fields.map((field, index) => {
      if (field.visibility && field.type === type) {
        return (
          <Grid
            item
            xs={12}
            md={4}
            xl={3}
            className={classes.fields}
            key={field.id}
          >
            <Typography className={classes.fieldNames}>
              {field.label}
            </Typography>
            <StyledTextField
              key={field.id}
              type={"number"}
              inputProps={{ step: "0.01" }}
              size="small"
              fullWidth
              {...register(`bodyCompositionFields.${index}.value` as const, {
                min: 0,
                required: false,
                max: field.name.includes("Percentage") ? 100 : undefined,
                validate: hasUpTo2Decimals,
              })}
            />
            {errors.bodyCompositionFields !== undefined
              ? errors.bodyCompositionFields[index] && (
                  <Typography
                    key={index}
                    variant="subtitle2"
                    color="error"
                    className={classes.error}
                  >
                    {getErrorMessage(
                      errors.bodyCompositionFields[index]?.value?.type
                    )}
                  </Typography>
                )
              : null}
          </Grid>
        );
      }
    });
  }

  function displayBodyComponentsSwitches(type: string) {
    return fields.map((field, index) => {
      if (field.type == type) {
        return (
          <Box display="flex" alignItems="flex-start" width="50%" key={index}>
            <StyledFormControlLabel
              value="end"
              control={
                <Switch
                  color="primary"
                  size="small"
                  checked={field.visibility}
                  onChange={(event) => {
                    const updatedValue = fields.map((e) => {
                      if (e.label === field.label) {
                        return { ...e, visibility: event.target.checked };
                      }
                      return e;
                    });
                    setValue("bodyCompositionFields", [...updatedValue]);
                  }}
                />
              }
              label={field.label}
              labelPlacement="end"
            />
          </Box>
        );
      }
    });
  }

  function displayDateField() {
    return (
      <Box width="100%" marginBottom={4}>
        <Box display="flex" alignItems="center">
          <Box>
            <Typography
              className={classes.scaleTextFieldLabel}
              color="textSecondary"
              gutterBottom
            >
              Date
            </Typography>
            <Controller
              control={control}
              name="date"
              render={({
                field: { ref, name, onChange, ...field },
                fieldState,
              }) => (
                <DatePicker
                  {...field}
                  inputRef={ref}
                  onChange={(value) => onChange(value as Date)}
                  slotProps={{
                    textField: {
                      name,
                      fullWidth: true,
                      error: !!fieldState?.error,
                      helperText: fieldState?.error?.message,
                    },
                  }}
                />
              )}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  if (loading) return null;

  return (
    <Modal
      open={visibility}
      onClose={() => {
        onClose(), reset();
      }}
      className={classes.root}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box display="flex" flexDirection="row">
          <Box
            className={cx(classes.modalBody, {
              [classes.customizeModalOpen]: customizeModalVisible,
              [classes.customizeModalClose]: !customizeModalVisible,
            })}
          >
            <Box className={classes.modalHeader}>
              <Typography variant="h6" color="textPrimary">
                {modeLabel} Body Compositions
              </Typography>
              <div>
                <StyledButton
                  variant="outlined"
                  startIcon={<SettingsIcon />}
                  onClick={() => setCustomizeModalVisible(true)}
                >
                  Customize
                </StyledButton>

                <IconButton
                  onClick={() => {
                    resetFields();
                    onClose();
                    setCustomizeModalVisible(false);
                  }}
                  size="large"
                >
                  <CloseIcon />
                </IconButton>
              </div>
            </Box>
            <Box display="flex" alignItems="center">
              {displayDateField()}
            </Box>
            {fields.length !== 0 ? (
              <>
                <Box className={classes.fieldWrapper}>
                  <Grid container spacing={4}>
                    {displayBodyCompositionFields("defaultField")}
                  </Grid>
                </Box>

                <Box className={classes.fieldWrapper}>
                  <Grid container spacing={4}>
                    {displayBodyCompositionFields("additionalField")}
                  </Grid>
                </Box>
              </>
            ) : (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                marginBottom={8}
              >
                <Typography variant="h6" color="textSecondary">
                  There are no visible fields anymore. Click the customize
                  button to set which field will be visible.
                </Typography>
              </Box>
            )}
            <Box className={classes.buttonContainer}>
              <StyledCancelButton
                variant="outlined"
                onClick={() => {
                  onClose();
                  resetFields();
                }}
              >
                Cancel
              </StyledCancelButton>
              <Button
                color="primary"
                disabled={fields.length == 0 || !isValid}
                type="submit"
              >
                {buttonLabel}
              </Button>
            </Box>
          </Box>

          <Slide
            direction="right"
            mountOnEnter
            unmountOnExit
            in={customizeModalVisible}
          >
            <Box className={classes.customizeModal}>
              <Box className={classes.modalHeaderExpanded}>
                <Typography
                  variant="h6"
                  color="textPrimary"
                  style={{ alignSelf: "center" }}
                >
                  Customize Data
                </Typography>
                <IconButton
                  onClick={() => setCustomizeModalVisible(false)}
                  size="large"
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              <Box className={classes.defaultBodyMeasurements}>
                <Typography
                  variant="subtitle1"
                  color="textPrimary"
                  className={classes.defaultBodyMeasurementsHeaderText}
                >
                  Default body compositions
                </Typography>
                <FormControl component="fieldset">
                  <FormGroup>
                    <Box className={classes.switchWrapper}>
                      {displayBodyComponentsSwitches("defaultField")}
                    </Box>
                  </FormGroup>
                </FormControl>
              </Box>

              <Box className={classes.defaultBodyMeasurements}>
                <Typography
                  variant="subtitle1"
                  color="textPrimary"
                  className={classes.defaultBodyMeasurementsHeaderText}
                >
                  Additional Body Compositions
                </Typography>
                <FormControl component="fieldset">
                  <FormGroup>
                    <Box className={classes.switchWrapper}>
                      {displayBodyComponentsSwitches("additionalField")}
                    </Box>
                  </FormGroup>
                </FormControl>
              </Box>

              <Box alignSelf={"center"}>
                <Button
                  variant="contained"
                  onClick={handleUpdateClientSettings}
                  disabled={isSavingSelection}
                >
                  Save Selection
                </Button>
                <StyledRestorButton
                  variant="outlined"
                  onClick={handleRestoreDefault}
                >
                  Restore Default
                </StyledRestorButton>
              </Box>
            </Box>
          </Slide>
        </Box>
      </form>
    </Modal>
  );
};

export default AddBodyCompositionModal;
